---
title: "Redefining Recycle"
subtitle: "with apologies to lee kopman"
date: 2025-05-04 22:24
---

import { Formation, FormationTable } from "#components/Formation";

Recently, I've been dancing some C3B and learning some C4, so square dancing's been on my mind. So let's talk about Recycle!

## Five different calls

Vic Ceder attributes most calls in the Recycle family to Lee Kopman, 1974. The lore is that:

- [All 8 Recycle](https://www.ceder.net/def/all8recycle.php?language=usa) was invented by Lee Kopman.

- Someone (probably Lee Kopman) noticed that centers and ends did separate things, so he split All 8 Recycle into [Recycle (from a wave)](https://www.ceder.net/def/recycle_ms.php?language=usa) and [Recycle (from facing couples).](https://www.ceder.net/def/recycle_a2.php)

- Then someone (probably Lee Kopman) split Recycle (from a wave) into three parts, which today we call [Recycle (by parts).](https://www.ceder.net/def/23recycle.php?language=usa&level=C1)

- Some time later, [Recycle (from a box)](https://www.ceder.net/def/boxrecycle.php?language=usa) was defined. Ceder *doesn't* attribute this to Kopman, interestingly enough.

- Then a year later, Joe Chiles invents [Cross Cycle,](https://www.ceder.net/def/crosscycle.php?language=usa) which can be done both from a wave and a box.

- Four years later, Kopman and Dave Hodson invent the related call [Linear Cycle.](https://www.ceder.net/def/linearcycle.php)

These form what I think of as "the Recycle family", and these calls are divided over several levels:

- Recycle (from a wave) is in Mainstream.
- Linear Cycle is in Plus, though only from waves; the full Linear Cycle is in A1.
- Recycle (from facing couples) is in A2.
- All 8 Recycle, Recycle (from a box), and Recycle (by parts) are all in C1.
- Cross Cycle is in C3B.

The trouble with Recycle (by parts), and the similarly defined parts of Cross Cycle, is that it's not at all how people dance it. For reference, the parts are:

1. Single Bounce the Centers.
2. Box Counter Rotate.
3. Roll.

<FormationTable
  content={[
    ["1^ 2v 3^ 4v", "before"],
    ["1^ 3v / 2^ 4v", "after 1/3"],
    ["2> 1> / 4< 3<", "after 2/3"],
    ["2v 1v / 4^ 3^", "after"],
  ]}
/>

Note that the real definition for the first part of Recycle is "centers Fold behind the ends and adjust to a box." Here I've used Single Bounce the centers (using the C2 call [Bounce](https://www.ceder.net/def/bounce.php?language=usa&level=C2)) to make the definition more precise. The analogous definition for Cross Cycle has Bounce the centers.

In any case, the call 2/3 Recycle is quite common in C1, but no one ever dances it as Single Bounce the centers; Box Counter Rotate. Instead, people dance it more like a [Flip the Line](https://www.ceder.net/def/fliptheline.php?language=usa) 1/2, or a Hinge; Circulate. Similarly, the full call Recycle (from a wave) doesn't feel like it has a Box Counter Rotate in it. It's most often danced as ends Cross Fold, while the centers walk in a small circle outward to end a small step behind where they started.

I've once heard someone say that 1/3 Recycle, 2/3 Recycle, and Recycle are all different calls, and none of them feel like they start the others. I'm inclined to agree. Add the disparate calls Recycle (from facing couples) and Recycle (from a box), and it feels like you have five different calls, all under the trenchcoat of the name "Recycle". Is there a way to unify these, or at least, make the parts of Recycle feel less weird?

## Start with a Hinge

Consider, instead, the following, alternate division of Recycle into parts:

1. Hinge.
2. Box Circulate.
3. 1/4 In.

<FormationTable
  content={[
    ["1^ 2v 3^ 4v", "before"],
    ["1> 3> / 2< 4<", "after 1/3"],
    ["2> 1> / 4< 3<", "after 2/3"],
    ["2v 1v / 4^ 3^", "after"],
  ]}
/>

This preserves 2/3 Recycle and Recycle, while feeling more like the dance motions for both. It comes with the consequence of having a different 1/3 Recycle, but I don't feel bad about that at all.

Linear Cycle could then be defined as Finally Use [Couple Up](https://www.ceder.net/def/coupleup.php?language=usa&level=C3A) Recycle. While Linear Cycle can currently be *described* as Secondly Twice Recycle, the redefinition makes both calls explicitly have Hinge as their first part, which relates the calls more. And doesn't Recycle *kinda* feel like it starts with a Hinge?

Also, Cross Cycle (from a two-faced line) could then be defined as Initially [Once Removed](https://www.ceder.net/def/onceremoved.php?language=usa&level=C2) Recycle. Then again, Cross Cycle (from a two-faced line) can already be defined in terms of Recycle: it's Initially Use Bounce the Centers Recycle. Both definitions have the same result for 2/3 Cross Cycle, though our redefinition is again closer to how it's danced.

In my mind, the biggest pro has to do with Recycle (from facing couples) and Recycle (from a box). I've learned from Andy Tockman that you can [redefine both](https://tck.mn/squares/definitions/) as:

1. Hinge.
2. Drag the Centers, Couples Hinge.

<FormationTable
  content={[
    ["1^ 2v / 4^ 3^", "before"],
    ["1> / 2< / 4> / 3<", "after 1/2"],
    ["3^ 4v 2^ 1v", "after"],
  ]}
/>

Not only does this redefinition unite both Recycle (from facing couples) and Recycle (from a box), but it also starts with a Hinge, which would then make it related to Recycle!

The "Drag the Centers" bit might seem strange, but consider that [Ah So](https://www.ceder.net/def/ahso.php?language=usa&level=C1) can already be defined as Drag the Centers, Wheel and Deal. And Ah So is also related to Recycle (from a wave), because the latter is Ah So; Like a Couple Up.

This redefinition makes Cross Cycle (from a box) *also* an Initially Once Removed Recycle, which unites both definitions of Cross Cycle into a single, snappy definition!

Well, kinda. While this definition works from an eight chain, it doesn't work from facing lines. What you really want is a "work Once Removed in your box," where you do a normal Hinge, and then reinterleave the dancers in a Once Removed manner. You could say this as [Slither,](https://www.ceder.net/def/slipslideswingslither.php) but that wouldn't be consistent with the Cross Cycle (from a two-faced line) definition.

## “Rewinding” Recycle

Alright, forget about everything we just talked about. Let's say we accepted the canonical, three-part definition of Recycle: Single Bounce the centers; Box Counter Rotate; Roll.

Even if you were fine with this part division, the calls Recycle (from facing couples) and Recycle (from a box) are still definitionally unrelated to Recycle (from a wave). Yet, from a right-hand wave, the sequence [Recycle (from a wave); Recycle (from facing couples)](https://www.tamtwirlers.org/taminations/#/?main=SEQUENCER&formation=Right+Hand+Waves&calls=Recycle;Recycle) dances smoothly, almost as if the second Recycle "cancels" the first Recycle to return to a wave. Can we make that feeling more precise?

It turns out we can, via the [Rewind](https://www.challengedance.org/rewind/rewind.html) concept. Well, kind of. You can't formally Rewind a Recycle (from a wave) because its last part has an ambiguous Rewind. We can, however, *choose* among the possible Rewinds, such that the call we get *is* Recycle (from a box). This gives us this three-part definition:

1. [Zig-Zag.](https://www.ceder.net/def/zigzag.php?language=usa&level=A2)
2. Box Counter Rotate.
3. [Inward Roll to a Wave.](https://www.ceder.net/def/rightrolltoawave.php?language=usa&level=A1)

<FormationTable
  content={[
    ["1v 2v / 4^ 3^", "before"],
    ["1> 2> / 4< 3<", "after 1/3"],
    ["4^ 1v / 3^ 2v", "after 2/3"],
    ["3^ 4v 2^ 1v", "after"],
  ]}
/>

Compare this to the three parts of Recycle (from a wave). If these were formal Rewinds, then each formation below would be related to the corresponding above by a U-Turn Back---we don't *quite* get that due to the ambiguous Rewind, but it's pretty close.

<FormationTable
  content={[
    ["3v 4^ 2v 1^", "before"],
    ["4v 1^ / 3v 2^", "after 1/3"],
    ["1< 2< / 4> 3>", "after 2/3"],
    ["1v 2v / 4^ 3^", "after"],
  ]}
/>


Note that this definition works for both Recycle (from facing couples) and Recycle (from a box). It doesn't dance like Recycle (from a box) at all, but this is also the case for the three-part definition of Recycle (from a wave), so I'll argue it's a feature.

The last part of this call, an Inward Roll to a Wave, also appears as a part of the C3A call [Rally](https://www.ceder.net/def/rally.php). It's a Rewind of the "centers Fold and adjust to a box" motion. Similarly, [Step and Fold](https://www.ceder.net/def/stepandfold.php) and 1/2 Zoom are also Rewinds of each other, which makes the call [Plenty](https://www.ceder.net/def/plenty.php?language=usa&level=C1) symmetric in a nice way.

Also, observe the similarity between this definition and the C3B call [Sidetrack.](https://www.ceder.net/def/sidetrack.php?language=usa&level=C3B) In fact, its callback "Zig-Zag, Counter Rotate, Roll" could also apply to this redefinition of Recycle (from a box), if you interpret *Roll* as meaning Inward Roll to a Wave.

Finally, the corresponding definition of Cross Cycle (from a box) changes the last part with: as one motion, Inward Roll to a Wave and Slither.

## As a tagging call

While 2/3 Recycle and Recycle (from a wave) are both common calls, 1/3 Recycle isn't called nearly as much. Bringing attention to the first part of Recycle (from a wave) with something like Oddly Tandem Recycle tends to trip people up. I think we can make this situation a bit better by making 1/3 Recycle a cohesive call on its own, something I also first heard from Tockman.

I'm also using the name they suggested, which is Tagcycle. The definition is:

> From a wave. Centers Step Thru and U-Turn Back, while the ends slide together. This is Tagcycle 1/4. Extend as needed.

<FormationTable
  content={[
    ["1^ 2v 3^ 4v", "before"],
    ["3v / 1^ 4v / 2^", "after 1/4"],
    ["1^ 3v / 2^ 4v", "after 1/2"],
    ["1^ / 2^ 3v / 4v", "after 3/4"],
    ["1^ / 2^ / 3v / 4v", "after"],
  ]}
/>

With this definition, Recycle (from a wave) is equivalent to [Reflected](https://www.ceder.net/def/reflected.php?language=usa&level=C3B) Tagcycle 1/2 and Roll. In particular, 1/3 Recycle is Tagcycle 1/2, and 2/3 Recycle is Reflected Tagcycle 1/2.

Tagcycle is similar to Flip the Line. Both are tagging calls that start from waves, with the same set of leaders and trailers after tagging. The difference is that a full Flip the Line ends in the same orientation as the original wave, while a full Tagcycle ends in the other orientation.

Note that the relationship between Tagcycle and Recycle is a similar relationship to Flip the Line and Linear Cycle: a Linear Cycle is equivalent to Reflected Flip the Line 1/2 and Roll.

There's some similarity with Tagcycle and the centers part of [Fall into a Column,](https://www.shabsin.com/~dana/squares/c4/book/calls/Fall_Into_a_Column.html) but I don't think I've otherwise seen this dance motion elsewhere.

Given the relationship with Tagcycle and Flip the Line, and the existence of the call Cross Flip the Line, we might wonder if we can analogously define a Cross Tagcycle. But the given definition of Tagcycle also works fine from a two-faced line:

<FormationTable
  content={[
    ["1^ 2^ 3v 4v", "before"],
    ["2v / 1^ 4v / 3^", "after 1/4"],
    ["1^ 2v / 3^ 4v", "after 1/2"],
    ["1^ / 3^ 2v / 4v", "after 3/4"],
    ["1^ / 3^ / 2v / 4v", "after"],
  ]}
/>

And indeed, from a two-faced line, Cross Cycle is equivalent to Reflected Tagcycle 1/2 and Roll! So we might as well expand our definition of Tagcycle to work from either a wave or a two-faced line.

To summarize the relationships we've discussed:

| **Tagging call**       | **Reflected 1/2 Tag and Roll**           |
| ---------------------- | ---------------------------------------- |
| Flip the Line          | Linear Cycle                             |
| Cross Flip the Line    | [Cross Linear Cycle][cross-linear-cycle] |
| Tagcycle (from a wave) | Recycle                                  |
| Tagcycle (from a 2FL)  | Cross Cycle                              |

I wouldn't go so far as to suggest that Reflected 1/2 Tag and Roll be its own tagging suffix, but it does feel like a flowy thing to do.

Alright. That's it. That's the post.

[cross-linear-cycle]: http://www.mixed-up.com/definitions/drawsvg.html#crosslinearcycle
